import React, { useEffect, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { logEvent, EVENTS } from '../services/AnalyticsService';
import { FaSpinner, FaTrash } from 'react-icons/fa';
import { getModuleRegistration } from '../moduleRegistry';

const ModuleLoader = () => (
  <div className="w-full h-32 flex items-center justify-center bg-white/10 backdrop-blur-md rounded-[2.5rem] border-2 border-dashed border-white/20">
    <FaSpinner className="animate-spin text-white/30 text-xl" />
  </div>
);

const ModuleRenderer = ({
  modules,
  isEditing,
  updateModule,
  removeModule,
  pageId,
  isLocked,
  openingMode = 'full_page',
  mediaUsage,
  focusedModuleId = '',
}) => {
  useEffect(() => {
    if (!pageId || isEditing) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target;
          logEvent(pageId, EVENTS.MODULE_VIEW, {
            moduleType: el.dataset.moduleType,
            moduleId: el.dataset.moduleId,
            position: parseInt(el.dataset.position) || 0,
          });
          observer.unobserve(el);
        }
      });
    }, { threshold: 0.5 });

    document.querySelectorAll('[data-module-type]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [modules, pageId, isEditing, openingMode]);

  if (!modules || !Array.isArray(modules)) return null;

  const renderModule = (module, index, options = {}) => {
    const moduleId = module.id || module._localId;
    if (!isEditing && module.type === 'ai_generator') return null;
    const {tier, Component} = getModuleRegistration(module.type);
    if (!Component) {
      if (!isEditing) return null;
      return (
        <motion.div
          key={moduleId}
          layout
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          className="relative w-full px-4 md:px-0 lumirae-module-shell lumirae-editing-module"
          id={`gift-module-${moduleId}`}
          data-module-type={module.type}
          data-module-tier="unknown"
          data-module-id={moduleId}
          data-position={index}
        >
          <div className="rounded-[1.5rem] border border-amber-200 bg-amber-50/90 px-4 py-4 text-left text-amber-900 shadow-sm">
            <div className="text-[10px] font-black uppercase tracking-[0.18em]">
              Legacy module preserved
            </div>
            <p className="mt-2 text-xs font-semibold leading-relaxed opacity-75">
              This gift contains an older module type: <strong>{module.type || 'unknown'}</strong>. The data is still saved,
              but this version does not have an editor for it yet.
            </p>
          </div>
        </motion.div>
      );
    }
    const isLegacy = tier === 'legacy';
    const canEditModule = isEditing && !isLegacy;
    const isFocused = focusedModuleId && focusedModuleId === moduleId;
    const isDimmed = isEditing && focusedModuleId && focusedModuleId !== moduleId;
    const onTemporarySave = (id, newData) => updateModule(id, newData);

    return (
      <motion.div
        key={moduleId}
        layout
        initial={{ opacity: 0, y: options.compact ? 12 : 28 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.34, ease: 'easeOut' }}
        className={`relative w-full px-4 md:px-0 lumirae-module-shell ${canEditModule ? 'lumirae-editing-module' : ''} ${canEditModule && isFocused ? 'lumirae-single-edit-module' : ''} ${isFocused ? 'lumirae-focused-module' : ''} ${isDimmed ? 'lumirae-dimmed-module' : ''}`}
        id={`gift-module-${moduleId}`}
        data-module-type={module.type}
        data-module-tier={tier}
        data-module-id={moduleId}
        data-position={index}
        data-focused={isFocused ? 'true' : 'false'}
      >
        {!isEditing && options.label && (
          <div className="mb-3 flex justify-center">
            <div className="inline-flex items-center gap-3 rounded-full border-2 border-white bg-white/60 px-4 py-2 text-[8px] font-black uppercase tracking-[0.28em] text-[#5A4A6B]/45 shadow-sm">
              {options.label}
            </div>
          </div>
        )}

        <Suspense fallback={<ModuleLoader />}>
          <Component
            moduleId={moduleId}
            data={module.data}
            isEditing={canEditModule}
            onTemporarySave={onTemporarySave}
            pageId={pageId}
            isLocked={isLocked}
            mediaUsage={mediaUsage}
            modules={modules}
          />
        </Suspense>

        {isEditing && isLegacy && (
          <div className="mt-4 rounded-[1.5rem] border border-amber-200 bg-amber-50/90 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-amber-800 shadow-sm">
            Legacy chapter: shown read-only for older gifts. Replace it with a current chapter to edit or upload media.
          </div>
        )}

        {isEditing && (
          <button
            onClick={() => removeModule(moduleId)}
            className="absolute -top-4 -right-4 w-10 h-10 bg-red-500 text-white rounded-full shadow-xl flex items-center justify-center hover:scale-110 transition-all z-50 border-4 border-white"
            title="Remove Module"
          >
            <FaTrash size={14} />
          </button>
        )}
      </motion.div>
    );
  };

  return (
    <div className={`flex flex-col w-full max-w-2xl mx-auto px-0 ${openingMode === 'quiet' ? 'gap-16' : 'gap-12'}`}>
      <AnimatePresence mode="popLayout">
        {modules.map((module, index) => renderModule(module, index, {
          label: openingMode === 'quiet' && !isEditing ? `Quiet Moment ${String(index + 1).padStart(2, '0')}` : null,
        }))}
      </AnimatePresence>
    </div>
  );
};

export default ModuleRenderer;
